import $ from 'jquery';
import * as PropTypes from 'prop-types';

import {assert} from 'Asserts';
import {areDataModsEnabledAnywhere, DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import * as BioentityDAO from 'bioentities/BioentityDAO';
import {Notation} from 'biojs/BioConstants';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  chemicalStructureInputFromCreateBioentityArgs,
  stripSaltsAndNotify,
} from 'chemistry/molecules/MoleculeUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {LabelingStrategy, TAG_SCHEMA_SHAPE, TagSchema} from 'constants/RegistryConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {Bioentity, SchemaTag, TAG_SHAPE} from 'folder-items/FileConstants';
import {
  ComplexPolymerConnection,
  ResolvedAttachmentInput,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  complexPolymerInputFromCreateBioentityArgs,
  ComplexPolymerSchemaConfigGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {
  complexPolymerHasAllComponentsForCreate,
  CreateComplexPolymerSchemaTag,
} from 'modbio/complex-polymers/create/CreateComplexPolymerUtil';
import {getRegisterMergeOnlyChangeSummary} from 'samples/getInventoryRegistryChangeSummary';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import {StudySearchItem} from 'studies/StudySearchInput';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {Optional} from 'util/Types';

export interface CreateBioentityArgs {
  name: string;
  folderId: Optional<string>;
  notation: Notation;
  customSyntaxId: Optional<string>;
  residues: Optional<string>;
  smiles: string;
  chemicalSketcherValue: ChemicalStructureDrawerValue;
  tagSchema: Optional<TagSchema>;
  tags: Array<SchemaTag>;
  studySearchItems: Optional<Array<StudySearchItem>>;
  complexPolymerTags: Optional<Array<CreateComplexPolymerSchemaTag>>;
  complexPolymerConnections: Optional<Array<ComplexPolymerConnection>>;
  complexPolymerResolvedAttachments: Optional<Array<ResolvedAttachmentInput>>;
  oligoDuplexOffset: number | null;
  complexPolymerSchemaConfig: ComplexPolymerSchemaConfigGraphQL | null;
}

export function getDefaultCreateBioentityArgs(): CreateBioentityArgs {
  return {
    name: '',
    folderId: null,
    notation: Notation.NATURAL_BASES,
    customSyntaxId: null,
    residues: null,
    smiles: '',
    chemicalSketcherValue: EMPTY_MOLFILE,
    tagSchema: null,
    tags: [],
    studySearchItems: [],
    complexPolymerTags: [],
    complexPolymerConnections: [],
    complexPolymerResolvedAttachments: [],
    oligoDuplexOffset: null,
    complexPolymerSchemaConfig: null,
  };
}

export interface CreateAndRegisterBioentityArgs extends Omit<CreateBioentityArgs, 'folderId'> {
  labelingStrategy: Optional<LabelingStrategy>;
  projectFolderId: Optional<string>;
}

export const CREATE_AND_REGISTER_BIOENTITY_ARGS_SHAPE = PropTypes.shape({
  name: PropTypes.string.isRequired,
  labelingStrategy: PropTypes.string,
  projectFolderId: PropTypes.string,
  tagSchema: TAG_SCHEMA_SHAPE,
  tags: PropTypes.arrayOf(TAG_SHAPE.isRequired).isRequired,
});

export function getDefaultCreateAndRegisterBioentityArgs(): CreateAndRegisterBioentityArgs {
  const {folderId, ...createBioentityArgs} = getDefaultCreateBioentityArgs();
  return {
    ...createBioentityArgs,
    projectFolderId: folderId,
    labelingStrategy: null,
  };
}

export function toCreateAndRegisterBioentityArgs(
  args: Partial<CreateBioentityArgs>
): Partial<CreateAndRegisterBioentityArgs> {
  const {folderId, ...createAndRegisterBioentityArgsWithoutProjectFolderId} = args;
  return folderId === undefined
    ? createAndRegisterBioentityArgsWithoutProjectFolderId
    : {...createAndRegisterBioentityArgsWithoutProjectFolderId, projectFolderId: folderId};
}

export function toCreateBioentityArgs(args: CreateAndRegisterBioentityArgs): CreateBioentityArgs {
  const {projectFolderId, ...createBioentityArgsWithoutFolderId} = args;
  return {...createBioentityArgsWithoutFolderId, folderId: projectFolderId};
}

export function isBioentityValidForCreation(args: CreateAndRegisterBioentityArgs): boolean {
  const {labelingStrategy, projectFolderId, tagSchema, complexPolymerSchemaConfig} = args;
  const folderIdRequired = BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES;
  const tagSchemaId = tagSchema ? tagSchema.id : null;
  const hasBothMolfileAndSmiles = args.smiles !== '' && args.chemicalSketcherValue !== EMPTY_MOLFILE;
  const isComplexPolymer =
    BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES &&
    complexPolymerSchemaConfig != null &&
    tagSchemaId != null &&
    complexPolymerSchemaConfig.schema.id === tagSchemaId;
  return Boolean(
    labelingStrategy &&
    (!folderIdRequired || projectFolderId != null) &&
    tagSchemaId &&
    !hasBothMolfileAndSmiles &&
    (!isComplexPolymer ||
      complexPolymerHasAllComponentsForCreate(complexPolymerSchemaConfig, args.complexPolymerTags ?? []))
  );
}

export function prepareForCreateAndRegisterBioentityServerArgs(
  args: CreateAndRegisterBioentityArgs,
  shouldErrorWhenMergingDuplicate: boolean,
  dataModsHeader?: DataModificationsHeader
): BioentityDAO.CreateAndRegisterBioentityServerArgs {
  assert(
    args.tagSchema != null,
    'expected tagSchema to be present when creating and registering a bioentity'
  );
  assert(
    (args.notation === Notation.CUSTOM_SYNTAX) === Boolean(args.customSyntaxId),
    'Invalid notation selection'
  );
  const chemicalStructureInput = chemicalStructureInputFromCreateBioentityArgs(args);
  const complexPolymerInput = complexPolymerInputFromCreateBioentityArgs(args);
  return {
    ...args,
    tagSchemaId: args.tagSchema.id,
    tags: getTagsToSet(args.tags),
    chemicalStructureInput,
    shouldErrorWhenMergingDuplicate,
    studyIds: args.studySearchItems?.map((studySearchItem) => studySearchItem.id),
    complexPolymerInput,
    dataModsHeader,
  };
}

export async function createAndRegister(
  args: CreateAndRegisterBioentityArgs,
  shouldErrorWhenMergingDuplicate: boolean,
  dataModsHeader?: DataModificationsHeader
): Promise<Bioentity> {
  try {
    LoadingScreen.show('Registering entity...');
    const bioentity = await BioentityDAO.createAndRegister(
      prepareForCreateAndRegisterBioentityServerArgs(args, shouldErrorWhenMergingDuplicate, dataModsHeader)
    );
    await afterCreateAndRegister(bioentity, args);
    return bioentity;
  } finally {
    LoadingScreen.hide();
  }
}

export async function createAndRegisterWithDataModsOnMerge(
  args: CreateAndRegisterBioentityArgs
): Promise<Bioentity | null> {
  if (!areDataModsEnabledAnywhere()) {
    return await createAndRegister(args, false);
  }
  try {
    return await createAndRegister(args, true);
  } catch (e) {
    if (BioentityDAO.isMergingDuplicateError(e)) {
      // Have to use this Deferred workaround because the underlying runModal call does not support a return
      // value from onRequestSubmit
      const duplicateId = e.registeredDuplicateLabels.apiId;
      const bioentityDeferred: JQueryDeferred<Bioentity | null> = $.Deferred();
      await callAsyncWithDataModifications({
        changeSummary: getRegisterMergeOnlyChangeSummary(1),
        onModalCancel: () => {
          bioentityDeferred.resolve(null);
        },
        onPerformAction: async (dataModsHeader?) => {
          const bioentity = await createAndRegister(args, false, dataModsHeader);
          bioentityDeferred.resolve(bioentity);
        },
        apiIds: [duplicateId],
        analyticsParams: {
          module: 'REGISTRY',
          actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
          modifiedAction: 'REGISTRY_MERGE',
          numModifiedTotalItems: 1,
          entryId: null,
        },
        useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
      });

      return await Promise.resolve<Bioentity | null>(bioentityDeferred);
    } else {
      throw e;
    }
  }
}

export function prepareForCreateBioentityServerArgs(
  args: CreateBioentityArgs
): BioentityDAO.CreateBioentityServerArgs {
  assert(args.tagSchema != null, 'expected tagSchema to be present when creating bioentity');
  assert(args.folderId != null, 'expected folderId to be present when creating bioentity');
  const chemicalStructureInput = chemicalStructureInputFromCreateBioentityArgs(args);
  return {
    ...args,
    tagSchemaId: args.tagSchema.id,
    folderId: args.folderId,
    tags: getTagsToSet(args.tags),
    chemicalStructureInput,
    studyIds: args.studySearchItems?.map((studySearchItem) => studySearchItem.id),
  };
}

export async function create(args: CreateBioentityArgs): Promise<Bioentity> {
  const bioentity = await BioentityDAO.create(prepareForCreateBioentityServerArgs(args));
  FileBrowserActions.openFile(bioentity.id);
  return bioentity;
}

async function afterCreateAndRegister(
  bioentity: Bioentity,
  args: CreateAndRegisterBioentityArgs
): Promise<void> {
  if (bioentity.tagSchema?.folderItemType === ModelTypeConstants.MOLECULE) {
    await stripSaltsAndNotify(ChemicalFormatType.MOLFILE, args.chemicalSketcherValue!);
  }
}
